// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAggregationData } from '@shared/advisory/aggregation/providers';
import {
  useHoldingsBacktest,
  type UseHoldingsBacktestArgs,
  usePeriodPicker,
} from '@shared/advisory/aggregation/hooks';
import { useMemo } from 'react';
import { HistValues } from '@shared/ui-kit';
import { usePortfoliosData } from '@shared/ui-kit/providers/portfolios-provider/portfolios-provider';

export const useBacktest = () => {
  const {
    status: holdingsStatus,
    benchmark,
    onChangeBenchmark,
    institutionId,
    onRefresh,
    accountsPositions,
    hasHoldings,
  } = useAggregationData();

  const periodPicker = usePeriodPicker(HistValues['3Y']);
  const { savedBenchmarks } = usePortfoliosData();

  const backtestPayload = useMemo<UseHoldingsBacktestArgs>(
    () => ({
      hist: periodPicker.period,
      holdings: accountsPositions.holdings,
      benchmark: [benchmark],
    }),
    [periodPicker.period, accountsPositions.holdings, benchmark],
  );

  const { analysis, backtest, kpis, returns, ...backtestQuery } =
    useHoldingsBacktest(backtestPayload);

  const status = useMemo<typeof holdingsStatus>(() => {
    const isLoading = [
      holdingsStatus === 'pending',
      backtestQuery.isLoadingBacktesting,
    ].some(Boolean);

    const isError = [holdingsStatus === 'rejected', backtestQuery.isError].some(
      Boolean,
    );

    const isFetching = [
      holdingsStatus === 'fetching',
      backtestQuery.isFetchingBacktesting,
    ].some(Boolean);

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [
    backtestQuery.isError,
    backtestQuery.isFetchingBacktesting,
    backtestQuery.isLoadingBacktesting,
    holdingsStatus,
  ]);

  return {
    analysis,
    backtest,
    kpis,
    returns,
    periodPicker,
    status,
    benchmark,
    onChangeBenchmark,
    hasHoldings: hasHoldings,
    institutionId,
    onRefresh,
    savedBenchmarks,
  } as const;
};
